/**
 * STARTING A CHAT ON A PROJECT PAGE. The tab bar's new-chat button and the
 * chat panel's "New Chat" created a chat for the host's current entity, the
 * old PDM product, which only /product pages set: on /projects/<id> both
 * did nothing (no request, no error; VC-e2e, 2026-10-21, on main too). On a
 * project route they start an RFLPTask attached to the project instead (the
 * host's createChatForProject) and open it in the side panel, the page's own
 * query kept (a bound ?branch= stays).
 */
import { useAtomValue } from 'jotai';
import { useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { nodeAtomFamily, graphStoreJotaiStore } from '@sourcepilot/framework/graph-store';
import { useAppHost } from '../../state/AppHostContext';

export const projectIdOf = (pathname: string | null | undefined): string | null =>
  pathname?.match(/^\/projects\/([^/?#]+)/)?.[1] ?? null;

/** The page with the new chat open beside it: its query kept, `chat` and
 *  `agent` set. */
export function projectChatHref(pathname: string, query: string, chatId: string): string {
  const params = new URLSearchParams(query);
  params.set('chat', chatId);
  params.set('agent', 'RFLPTask');
  return `${pathname}?${params.toString()}`;
}

/** On a project route with a host that can make one: `start` creates and
 *  opens the chat (false when it failed, which `failed` then says); else
 *  null, and the caller keeps its own path. */
export function useStartProjectChat(): { projectId: string; start: () => Promise<boolean>; busy: boolean; failed: boolean } | null {
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const router = useRouter();
  const { createChatForProject } = useAppHost();
  const projectId = projectIdOf(pathname);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // one chat at a time, whatever render a caller holds
  const inFlight = useRef(false);
  // the project's name, for the chat's (the page has loaded the project)
  const project = useAtomValue(
    useMemo(() => nodeAtomFamily(projectId ?? '__none__'), [projectId]),
    { store: graphStoreJotaiStore },
  ) as { name?: unknown } | null;
  if (!projectId || !createChatForProject) return null;
  const start = async () => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true); setFailed(false);
    try {
      const made = await createChatForProject(projectId, typeof project?.name === 'string' ? project.name : null);
      if (!made?.id) { setFailed(true); return false; }
      router.push(projectChatHref(pathname, searchParams?.toString() ?? '', made.id));
      return true;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return { projectId, start, busy, failed };
}
